/**
 * LoyaltyProgramPage - Customer-facing loyalty program page
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { 
  ArrowLeft, Crown, Star, Award, Sparkles, Gift, 
  Users, TrendingUp, CheckCircle, ArrowRight, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';
import LoyaltyDashboard from '../../components/events/LoyaltyDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoyaltyProgramPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user has email stored
    const storedEmail = localStorage.getItem('user_email');
    if (storedEmail) {
      setEmail(storedEmail);
      setIsLoggedIn(true);
    }
    
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/loyalty/tiers`);
      if (response.ok) {
        const data = await response.json();
        setTiers(data);
      }
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    localStorage.setItem('user_email', email);
    setIsLoggedIn(true);
    toast.success('Logged in successfully!');
  };

  const handleLogout = () => {
    localStorage.removeItem('user_email');
    setIsLoggedIn(false);
    setEmail('');
  };

  const tierIcons = {
    bronze: Award,
    silver: Star,
    gold: Crown,
    platinum: Sparkles
  };

  const tierColors = {
    bronze: 'from-amber-500 to-amber-600',
    silver: 'from-gray-400 to-gray-500',
    gold: 'from-yellow-400 to-yellow-500',
    platinum: 'from-purple-500 to-purple-600'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white hover:bg-white/10 mb-4"
            onClick={() => navigate('/events')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Events
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Gift className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Loyalty Program</h1>
              <p className="text-purple-100 mt-1">
                Earn points, unlock rewards, and get exclusive benefits
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {isLoggedIn ? (
          <>
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-600">
                Logged in as <strong>{email}</strong>
              </p>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                Switch Account
              </Button>
            </div>
            <LoyaltyDashboard userEmail={email} />
          </>
        ) : (
          <>
            {/* Login Section */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Access Your Rewards</CardTitle>
                <CardDescription>
                  Enter your email to view your loyalty points and rewards
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    className="flex-1"
                  />
                  <Button onClick={handleLogin}>
                    View My Points
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Use the same email you use for bookings
                </p>
              </CardContent>
            </Card>

            {/* How It Works */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4">How It Works</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-purple-600">1</span>
                    </div>
                    <h3 className="font-semibold mb-2">Book Events</h3>
                    <p className="text-sm text-gray-600">
                      Earn 1 point per £1 / Rs.100 spent on any booking
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-purple-600">2</span>
                    </div>
                    <h3 className="font-semibold mb-2">Level Up</h3>
                    <p className="text-sm text-gray-600">
                      Unlock higher tiers for better discounts and perks
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-purple-600">3</span>
                    </div>
                    <h3 className="font-semibold mb-2">Redeem</h3>
                    <p className="text-sm text-gray-600">
                      Use points for discounts on future bookings
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Tier Showcase */}
            {tiers && (
              <div>
                <h2 className="text-xl font-bold mb-4">Membership Tiers</h2>
                <div className="grid md:grid-cols-4 gap-4">
                  {Object.entries(tiers.tiers).map(([tierKey, tierData]) => {
                    const TierIcon = tierIcons[tierKey] || Award;
                    const gradient = tierColors[tierKey] || 'from-gray-400 to-gray-500';
                    
                    return (
                      <Card key={tierKey} className="overflow-hidden">
                        <div className={`bg-gradient-to-br ${gradient} p-4 text-white`}>
                          <TierIcon className="w-8 h-8 mb-2" />
                          <h3 className="font-bold capitalize text-lg">{tierKey}</h3>
                          <p className="text-white/80 text-sm">
                            {tierData.min_points.toLocaleString()}+ points
                          </p>
                        </div>
                        <CardContent className="p-4">
                          {tierData.discount_pct > 0 && (
                            <Badge className="bg-green-100 text-green-700 mb-2">
                              {tierData.discount_pct}% OFF All Bookings
                            </Badge>
                          )}
                          <ul className="space-y-1">
                            {tierData.benefits.map((benefit, idx) => (
                              <li key={idx} className="text-sm text-gray-600 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Referral Program */}
            <Card className="mt-8 border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-blue-100 rounded-full">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-blue-900">Refer Friends, Earn More!</h3>
                    <p className="text-blue-700">
                      Get <strong>500 points</strong> for each friend who joins.
                      They get <strong>10% off</strong> their first booking!
                    </p>
                  </div>
                  <Button 
                    onClick={() => {
                      if (email) {
                        handleLogin();
                      } else {
                        toast.info('Enter your email to get your referral code');
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Get Your Code
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
